import { observer } from "mobx-react-lite";
import { FunctionComponent } from "react";

import { TIME_FORMATS } from "@bps/date-time";
import {
  DirectionalHint,
  FontSizes,
  Heading,
  IDropdownOption,
  MessageBar,
  MessageBarType,
  Persona,
  PersonaSize,
  Spinner,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { KeyTextValue } from "@libs/api/ref-data/RefDataAccessor.ts";
import {
  OutboundCommChannel,
  OutboundCommType
} from "@libs/gateways/comms/CommsGateway.dtos.ts";
import { nameOfFactory } from "@libs/utils/name-of.utils.ts";
import { useBookingCalendarScreenContext } from "@modules/booking/screens/booking-calendar/context/BookingCalendarScreenContext.tsx";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { OutboundCommTemplate } from "@stores/comms/models/OutboundCommTemplate.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Contact } from "@stores/practice/models/Contact.ts";
import { ErrorAlert } from "@ui-components/Alert.tsx";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { DropdownField } from "@ui-components/form/DropdownField.tsx";
import { FieldCondition } from "@ui-components/form/FieldCondition.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";
import { TextInputField } from "@ui-components/form/TextInputField.tsx";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";

import { SmsCharacterCount } from "./SmsCharacterCount.tsx";
import {
  SendReminderFormValues,
  SendReminderValidator
} from "./validators/SendReminderValidator.tsx";

const validator = new SendReminderValidator();
const TITLE = "Send SMS";

const nameOf = nameOfFactory<SendReminderFormValues>();

export const SendReminderDialog: FunctionComponent = observer(() => {
  const { booking, notification, userExperience } = useStores();
  const theme = useTheme();
  const { reminderArgs, setReminderArgs } = useBookingCalendarScreenContext();

  if (!reminderArgs?.calendarEventId) {
    return null;
  }

  return (
    <DataFetcher<[CalendarEvent, OutboundCommTemplate[], Contact | undefined]>
      key={reminderArgs.calendarEventId}
      fetch={async ({ booking, comms, practice }) => {
        return Promise.all([
          booking.getCalendarEvent(reminderArgs.calendarEventId),
          comms.getAllOutboundCommTemplates()
        ]).then(async ([calendarEvent, templates]) => {
          const lookupId = !!calendarEvent.isGroupAppointment
            ? reminderArgs.contactId
            : calendarEvent.contactId;

          const contact = !!lookupId
            ? await practice.getContact(lookupId, {
                includeContactPreferences: true
              })
            : undefined;

          return [calendarEvent, templates, contact];
        });
      }}
    >
      {([calendarEvent, templates, contact]) => {
        const { core } = useStores();

        const defaultTemplate = templates
          .filter(x => x.outboundCommTypeCode === OutboundCommType.ApptReminder)
          .find(x => x.isDefault);

        const isMultipleAttendeeReminder =
          calendarEvent.isGroupAppointment && !reminderArgs.contactId;

        const noApptReminderConsent =
          contact?.contactPreferences?.apptRemPreferences
            ?.preferredCommChannelTypeCode === undefined;

        const isOptOut =
          contact?.contactPreferences?.apptRemPreferences?.contactHasOptedOut ??
          false;

        const noSMSReminder =
          !isMultipleAttendeeReminder && (noApptReminderConsent || isOptOut);

        const initialValues = {
          templateId: noSMSReminder ? "" : defaultTemplate?.id ?? ""
        };

        const templateValues = (): KeyTextValue[] => {
          return templates
            .filter(
              x => x.outboundCommTypeCode === OutboundCommType.ApptReminder
            )
            .map(({ id, name, isDefault }) => ({
              key: id,
              text: isDefault ? `${name} (default)` : name,
              data: { isDefault, name }
            }));
        };

        const onRenderOption = (option: IDropdownOption): JSX.Element => {
          return (
            <span>
              {option.data.name}
              {option.data.isDefault && (
                <Text
                  styles={{
                    root: { color: theme.palette.blackTranslucent40 }
                  }}
                >
                  {" (default)"}
                </Text>
              )}
            </span>
          );
        };

        const onSubmit = (values: SendReminderFormValues) =>
          booking.sendCalendarEventReminder({
            calendarEventId: calendarEvent.id,
            contactId: reminderArgs.contactId,
            content: values.reminderMessage!,
            campaignId:
              values.templateId === "" ? undefined : values.templateId,
            commType:
              values.templateId === ""
                ? OutboundCommType.ApptFree
                : OutboundCommType.ApptReminder
          });

        const onSubmitSucceeded = () => {
          notification.success(
            contact
              ? `Your message to ${contact.name} has been queued for sending`
              : `Your messages for ${
                  calendarEvent.groupDescription ??
                  `${calendarEvent.startDateTime.toFormat(
                    TIME_FORMATS.DEFAULT_TIME_FORMAT
                  )} ${calendarEvent.appointmentType?.name}`
                } participants have been queued for sending`
          );

          setReminderArgs(undefined);
        };
        return (
          <SubmissionFormDialog
            dialogName={TITLE}
            onSubmit={onSubmit}
            onSubmitSucceeded={onSubmitSucceeded}
            keepDirtyOnReinitialize
            buttonsProps={{
              submitButtonProps: {
                text: "Send",
                iconProps: {}
              },
              hideButtonsSeparator: true
            }}
            validate={values => validator.validate(values)}
            initialValues={initialValues}
            dialogProps={{
              minWidth: 480,
              onDismiss: () => setReminderArgs(undefined),
              dialogContentProps: {
                title: <Heading variant="modal-heading">{TITLE}</Heading>,
                showCloseButton: true
              }
            }}
          >
            {renderProps => (
              <Stack tokens={{ childrenGap: 16 }}>
                <Stack tokens={{ childrenGap: 8 }}>
                  <Persona
                    text={contact?.name ?? "Multiple participants"}
                    id={contact?.id}
                    size={PersonaSize.size24}
                    imageInitials={
                      contact?.initials ??
                      calendarEvent.activeAttendees.length.toString()
                    }
                  />
                  <Text>
                    {`${calendarEvent.startDateTime.toFormat(
                      TIME_FORMATS.DEFAULT_TIME_FORMAT
                    )} - ${calendarEvent.endDateTime.toFormat(
                      TIME_FORMATS.DEFAULT_TIME_FORMAT
                    )}`}
                  </Text>
                </Stack>
                <DropdownField
                  name={nameOf("templateId")}
                  placeholder="Select a template"
                  label="SMS template"
                  options={templateValues()}
                  calloutProps={{
                    calloutMaxHeight: 400,
                    directionalHint: DirectionalHint.bottomCenter
                  }}
                  onRenderOption={onRenderOption}
                  disabled={noSMSReminder}
                />
                {isMultipleAttendeeReminder && (
                  <MessageBar messageBarType={MessageBarType.warning}>
                    SMS consent may not be configured for all participants.
                  </MessageBar>
                )}
                {noSMSReminder && !isMultipleAttendeeReminder && (
                  <MessageBar messageBarType={MessageBarType.warning}>
                    {isOptOut
                      ? `${usePatientLabel(
                          true
                        )} opted out of appointment reminders.`
                      : `SMS consent has not been configured for this ${usePatientLabel(
                          false
                        )}.`}
                  </MessageBar>
                )}
                <FieldCondition<SendReminderFormValues["templateId"]>
                  when={nameOf("templateId")}
                  is={value => !!value}
                >
                  <DataFetcher
                    refetchId={renderProps.values.templateId}
                    noExceptionsHandlers
                    fetch={async () => {
                      if (renderProps.values.templateId) {
                        const renderedContent =
                          await userExperience.getCampaignRender(
                            renderProps.values.templateId,
                            {
                              channelCode: OutboundCommChannel.Sms,
                              calendarEventId: reminderArgs.calendarEventId,
                              contactId: reminderArgs.contactId
                            }
                          );
                        return renderedContent ?? "";
                      }
                      return "";
                    }}
                  >
                    {(messageContent, loading, error) => (
                      <Stack>
                        <Heading labelPaddings>Content</Heading>
                        <div
                          style={{
                            borderWidth: 1,
                            height: 120,
                            maxHeight: 120,
                            borderRadius: 2,
                            borderStyle: "solid",
                            borderColor: theme.palette.neutralDark,
                            padding: 4
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              fontSize: FontSizes.small,
                              whiteSpace: "pre-line",
                              padding: 8,
                              borderRadius: 2,
                              overflowY: "auto",
                              backgroundColor:
                                theme.semanticColors.disabledBackground
                            }}
                          >
                            {loading ? <Spinner /> : messageContent}
                          </div>
                        </div>

                        <div style={{ marginTop: 8 }}>
                          <SmsCharacterCount
                            count={messageContent?.length ?? 0}
                          />
                        </div>
                        {error && <ErrorAlert error={error} />}
                      </Stack>
                    )}
                  </DataFetcher>
                </FieldCondition>
                <FieldCondition when={nameOf("templateId")} is="">
                  <Stack tokens={{ childrenGap: 8 }}>
                    <TextInputField
                      name={nameOf("reminderMessage")}
                      label="Content"
                      multiline
                      rows={4}
                      resizable={false}
                      placeholder={
                        core.isNZTenant
                          ? "No template selected"
                          : "Enter details"
                      }
                      autoFocus={true}
                      disabled={core.isNZTenant}
                    />
                    <SmsCharacterCount
                      count={renderProps.values.reminderMessage?.length ?? 0}
                    />
                  </Stack>
                </FieldCondition>
              </Stack>
            )}
          </SubmissionFormDialog>
        );
      }}
    </DataFetcher>
  );
});
